import { useCallback, useEffect } from 'react';
import { createReducer as createReducerHook } from 'react-use';
import thunk from 'redux-thunk';

import createReducer from 'utils/createReducer';
import useWebsockets from 'utils/hooks/useWebsockets';

import { useTasksFiltersContext } from 'modules/tasks/filters/context';
import { fetchTasks } from 'modules/tasks/http';

import { tasksWorker } from 'modules/tasks/lists/utils';

const worker = tasksWorker;

const useReducerWithMiddleware = createReducerHook(thunk);

const FETCHING_TASKS = 'FETCHING_TASKS';
const FETCHING_MORE_TASKS = 'FETCHING_MORE_TASKS';
const TASKS_LOADED = 'TASKS_LOADED';

const setFetchingTasks = (payload) => ({
  type: FETCHING_TASKS,
  payload,
});

const setTasksLoaded = (payload) => ({
  type: TASKS_LOADED,
  payload,
});

const setLoadingMore = (payload) => ({
  type: FETCHING_MORE_TASKS,
  payload,
});

const computeNewTasksPage = (newPage) => {
  return async (dispatch, getState) => {
    try {
      const { tasks } = getState();
      const { data } = await worker.postMessage({
        fn: 'handleNewTasksPageComputation',
        data: { tasks, newPage },
      });
      const { taskList, tasksGrouped } = data || {};

      dispatch(setTasksLoaded({ taskList, tasksGrouped }));
    } catch (e) {
      console.error(e);
    }
  };
};

const computeRealTimeTask = (newGroupOfTasks) => {
  return async (dispatch, getState) => {
    try {
      const { filters, tasks } = getState();

      const { data } = await worker.postMessage({
        fn: 'handleRealTimeTaskComputation',
        data: {
          newGroupOfTasks,
          tasks,
          filters,
        },
      });

      if (!data) return;

      const { taskList, tasksGrouped } = data;
      dispatch(setTasksLoaded({ taskList, tasksGrouped }));
    } catch (e) {
      console.error(e);
    }
  };
};

const INIT_STATE = { filters: {}, tasks: [], groupedTasks: {}, loading: false, loadingMore: false };

const reducer = createReducer(INIT_STATE, {
  [FETCHING_TASKS](state, payload) {
    return { ...state, groupedTasks: {}, tasks: [], loading: true, filters: payload };
  },
  [FETCHING_MORE_TASKS](state, payload) {
    return { ...state, loadingMore: payload };
  },
  [TASKS_LOADED](state, payload) {
    const { taskList, tasksGrouped } = payload || {};
    return {
      ...state,
      tasks: taskList,
      groupedTasks: tasksGrouped,
      loading: false,
    };
  },
});

const useTasks = () => {
  const [state, dispatch] = useReducerWithMiddleware(reducer, INIT_STATE);
  const { filters } = useTasksFiltersContext();

  const handleRealTimeMessages = useCallback((event) => {
    dispatch(computeRealTimeTask(event));
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    let cancelled = false;
    dispatch(setFetchingTasks(filters));

    (async function getAllTasksPages({ pageNumber, filters }) {
      if (cancelled) return;
      if (pageNumber > 1) dispatch(setLoadingMore(true));
      try {
        const response = await fetchTasks({ pageNumber, filters });
        dispatch(setLoadingMore(false));

        if (!cancelled) dispatch(computeNewTasksPage(response.results));
        if (response.next) {
          await getAllTasksPages({ pageNumber: pageNumber + 1, filters });
        }
      } catch (e) {
        console.error(e);
      }
    })({ pageNumber: 1, filters });

    return () => {
      cancelled = true;
    };
  }, [filters, dispatch]);

  useWebsockets({
    channel: 'journey_summaries',
    event: 'journey_summary_updated',
    eventHandler: handleRealTimeMessages,
  });

  return {
    tasksNotAttended: state.groupedTasks?.NOT_ATTENDED,
    tasksAttended: state.groupedTasks?.KEEP_AN_EYE,
    loading: state.loading,
    loadingMore: state.loadingMore,
  };
};

export { useTasks };
